'use client';
import React, { useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

// Booked date ranges (mock data)
// BACKEND INTEGRATION: GET /api/apartments/apt-001/availability
const bookedRanges = [
  { start: new Date(2026, 4, 18), end: new Date(2026, 4, 23) },
  { start: new Date(2026, 4, 28), end: new Date(2026, 5, 2) },
  { start: new Date(2026, 5, 10), end: new Date(2026, 5, 15) },
];

function isBooked(date: Date): boolean {
  return bookedRanges.some(
    (range) => date >= range.start && date <= range.end
  );
}

function isSameDay(a: Date, b: Date): boolean {
  return a.getDate() === b.getDate() && a.getMonth() === b.getMonth() && a.getFullYear() === b.getFullYear();
}

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];
const DAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function CalendarMonth({ year, month }: { year: number; month: number }) {
  const today = new Date(2026, 4, 14);

  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const startPad = (firstDay.getDay() + 6) % 7; // Mon=0

  const days: (Date | null)[] = [];
  for (let i = 0; i < startPad; i++) days.push(null);
  for (let d = 1; d <= lastDay.getDate(); d++) days.push(new Date(year, month, d));

  return (
    <div>
      <p className="text-sm font-700 text-foreground mb-3">{MONTH_NAMES[month]} {year}</p>
      <div className="grid grid-cols-7 gap-0.5 mb-1">
        {DAY_NAMES.map((d) => (
          <div key={`dayname-${d}`} className="text-center text-[10px] font-700 uppercase tracking-wider text-muted-foreground py-1">
            {d}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-0.5">
        {days.map((day, idx) => {
          if (!day) return <div key={`pad-${month}-${idx}`} />;
          const booked = isBooked(day);
          const past = day < today && !isSameDay(day, today);
          return (
            <div
              key={`day-${month}-${day.getDate()}`}
              className={`
                aspect-square flex items-center justify-center text-xs font-500 rounded-lg
                ${past ? 'text-muted-foreground/40 cursor-not-allowed' :
                  booked ? 'bg-red-100 text-red-400 line-through cursor-not-allowed': 'hover:bg-primary/15 hover:text-primary cursor-pointer text-foreground transition-colors'}
              `}
              title={booked ? 'Not available' : past ? '' : 'Available'}
            >
              {day.getDate()}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function AvailabilityCalendar() {
  const [monthOffset, setMonthOffset] = useState(0);
  const baseYear = 2026;
  const baseMonth = 4; // May

  const month1 = (baseMonth + monthOffset) % 12;
  const year1 = baseYear + Math.floor((baseMonth + monthOffset) / 12);
  const month2 = (baseMonth + monthOffset + 1) % 12;
  const year2 = baseYear + Math.floor((baseMonth + monthOffset + 1) / 12);

  return (
    <div>
      <h2 className="text-xl font-700 text-foreground mb-5">Availability</h2>
      <div className="card-base p-6">
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => setMonthOffset((o) => Math.max(0, o - 1))}
            disabled={monthOffset === 0}
            className="p-2 rounded-lg hover:bg-muted disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronLeft size={18} />
          </button>
          <div className="flex gap-1">
            <span className="w-3 h-3 rounded bg-green-200 inline-block" />
            <span className="text-xs text-muted-foreground mr-3">Available</span>
            <span className="w-3 h-3 rounded bg-red-100 inline-block" />
            <span className="text-xs text-muted-foreground">Booked</span>
          </div>
          <button
            onClick={() => setMonthOffset((o) => o + 1)}
            className="p-2 rounded-lg hover:bg-muted transition-colors"
          >
            <ChevronRight size={18} />
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <CalendarMonth year={year1} month={month1} />
          <CalendarMonth year={year2} month={month2} />
        </div>

        <p className="text-xs text-muted-foreground mt-5 text-center">
          Minimum stay: 3 nights · Check-in: 15:00 · Check-out: 11:00
        </p>
      </div>
    </div>
  );
}